import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { messages } from './messages.ts';
const useRequestToEditContentRequestPageAccessMutation = graphql`
	mutation useRequestToEditContentRequestPageAccessMutation(
		$requestPageAccessInput: RequestPageAccessInput!
	) {
		requestPageAccess(requestPageAccessInput: $requestPageAccessInput) {
			displayName
		}
	}
`;

const REQUEST_SENT_FLAG_ID = 'request-to-edit-sent';

export type TriggerAnalyticsEventType = (
	eventName: string,
	extraData?: {
		[key: string]: any;
	},
) => Promise<void>;

export type RequestToEditContentProps = {
	contentId: string | undefined | null;
	flags?: FlagsStateContainer;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
};

export const useRequestToEditContent = ({
	contentId,
	flags,
	triggerAnalyticsEvent,
}: RequestToEditContentProps) => {
	const [requestSent, setRequestSent] = useState<boolean>(false);
	const intl = useIntl();

	const showRequestToEditFailedFlag = useCallback(() => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('content.request.to.edit.failed');
		}

		if (!flags) {
			return;
		}
		void flags.showWarningFlag({
			title: intl.formatMessage(messages.notSentFlagTitle),
			description: intl.formatMessage(messages.notSentFlagDescription),
			appearance: 'warning',
			isAutoDismiss: true,
		});
	}, [flags, intl, triggerAnalyticsEvent]);

	const showRequestToEditSentFlag = useCallback(() => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('content.request.to.edit.success');
		}

		if (!flags) {
			return;
		}
		void flags.showSuccessFlag({
			id: REQUEST_SENT_FLAG_ID,
			title: intl.formatMessage(messages.requestSentFlagShortTitle),
			description: intl.formatMessage(messages.requestSentFlagDescription),
			isAutoDismiss: true,
		});
	}, [flags, intl, triggerAnalyticsEvent]);

	const handleRequestComplete = (result: any) => {
		if (result?.requestPageAccess) {
			setRequestSent(true);
			showRequestToEditSentFlag();
		} else {
			showRequestToEditFailedFlag();
			setRequestSent(false);
		}
	};
	const handleRequestError = () => {
		showRequestToEditFailedFlag();
		setRequestSent(false);
	};
	const [requestToEditContent, { loading }] = useMutationInterop<any, any>(
		useRequestToEditContentRequestPageAccessMutation,
		{
			variables: {
				requestPageAccessInput: {
					pageId: contentId || '',
					accessType: 'EDIT',
				},
			},
			onCompleted: handleRequestComplete,
			onError: handleRequestError,
		},
	);

	return {
		requestToEditContent,
		loading,
		hasRequestedEditPermission: requestSent,
	};
};
